import type { TFunction } from 'i18next';
import type { ForecastReviewAlert } from '@/services/forecastReviewService';
import { resolveUiLocale } from '@/utils/uiLocale';

type AlertLike = Pick<ForecastReviewAlert, 'alert_type' | 'message' | 'payload'>;

/** DB `message` codes use commas; locale keys use `__` (e.g. zero_sales_alert__declining_demand_trend). */
export function messageCodeToLocaleKey(code: string): string {
  return code.trim().replace(/,/g, '__');
}

function formatPct(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const pct = Math.abs(n) <= 1 ? n * 100 : n;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(0)}%`;
}

function formatQty(value: number | null | undefined, locale: string): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value).toLocaleString(locale, { maximumFractionDigits: 1 });
}

function payloadString(payload: Record<string, unknown> | undefined, key: string): string {
  const raw = payload?.[key];
  if (raw == null) return '';
  return String(raw).trim();
}

/** Resolve agent reason code stored in `message` (or payload fallbacks). */
export function resolveAlertMessageCode(alert: AlertLike): string {
  const fromMessage = (alert.message || '').trim();
  if (fromMessage) return fromMessage;

  const payload = (alert.payload ?? {}) as Record<string, unknown>;
  return (
    payloadString(payload, 'eol_reason') ||
    payloadString(payload, 'anomaly_reason') ||
    ''
  );
}

function interpolationForCode(
  code: string,
  payload: Record<string, unknown>,
  locale: string,
): Record<string, string> {
  const declineRaw =
    (payload.yoy_decline_pct as number | undefined) ??
    (payload.half_half_decline_pct as number | undefined);

  return {
    pct: formatPct(payload.relative_change as number | undefined) ?? '—',
    prev: formatQty(payload.prev_mean as number | undefined, locale) ?? '—',
    next: formatQty(payload.new_mean as number | undefined, locale) ?? '—',
    declinePct: formatPct(declineRaw) ?? '—',
    zeroPeriods:
      payload.trailing_zero_periods != null ? String(payload.trailing_zero_periods) : '—',
    code,
  };
}

export function getForecastAlertTypeLabel(
  alert: Pick<ForecastReviewAlert, 'alert_type' | 'alert_type_label'>,
  t: TFunction,
): string {
  const key = `forecastReview.alerts.types.${alert.alert_type}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return alert.alert_type_label ?? alert.alert_type;
}

/**
 * Maps each distinct DB `forecast_alert.message` code to a business-facing i18n string.
 * Distinct codes in DB (forecast_review scope): forecast_drop, forecast_spike,
 * declining_demand_trend, zero_sales_alert, zero_sales_alert,declining_demand_trend.
 */
export function getForecastAlertBusinessMessage(alert: AlertLike, t: TFunction): string {
  const code = resolveAlertMessageCode(alert);
  const payload = (alert.payload ?? {}) as Record<string, unknown>;
  const locale = resolveUiLocale(t('forecastReview.alerts.messageLocale'));

  if (code) {
    const localeKey = messageCodeToLocaleKey(code);
    const i18nKey = `forecastReview.alerts.messages.byCode.${localeKey}`;
    const translated = t(i18nKey, interpolationForCode(code, payload, locale));
    if (translated !== i18nKey) return translated;
  }

  return t('forecastReview.alerts.messages.fallback', {
    code: code || alert.alert_type,
  });
}
